import React, { useEffect, useState, useMemo, useRef } from "react";
import { db, storage } from "../firebaseConfig";
import { doc, setDoc, getDoc, collection, getDocs, query, where, deleteDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import ImageCropModal from "./ImageCropModal";
import { useGlobalCategories } from "../hooks/useGlobalCategories";
import { getTaskStepSlots, getTaskStepKeys, getStepDisplayLabel, type StepKey, type StepSlot } from "../utils/stepHelpers";

type TaskKind = 'single' | 'expiration' | 'before_after' | 'custom';
// StepKey is now string (from stepHelpers), keeping MediaRole for backward compatibility in some places
type MediaRole = 'primary' | 'expiration_label' | 'after';

interface SampleMedia {
  [stepKey: StepKey]: string[] | undefined;
  // Legacy support
  primary?: string[];
  expiration_label?: string[];
  after?: string[];
}

interface SubmissionStep {
  title: string;
  description: string;
  examples: string[];
}

interface SubmissionSpec {
  mediaSlots: Array<{ role: StepKey; required?: boolean; prompt?: string }>;
  questions?: Array<{ id: string; type: string; prompt: string; required: boolean; options: Array<{ label: string; value: string }> }>;
}

function buildDefaultSubmissionSpec(kind: TaskKind): SubmissionSpec {
  if (kind === 'expiration') {
    return {
      mediaSlots: [
        { role: 'primary', required: true, prompt: 'Front label photo' },
        { role: 'expiration_label', required: true, prompt: 'Expiration date close-up' },
      ],
      questions: [
        {
          id: 'exp_location',
          type: 'select',
          prompt: 'Where is the expiration date located?',
          required: true,
          options: [
            { label: 'Front', value: 'front' },
            { label: 'Back', value: 'back' },
            { label: 'Left side', value: 'left' },
            { label: 'Right side', value: 'right' },
            { label: 'Top', value: 'top' },
            { label: 'Bottom', value: 'bottom' },
            { label: 'Cap / Lid', value: 'cap' },
            { label: 'Other', value: 'other' },
          ]
        }
      ]
    };
  }
  if (kind === 'before_after') {
    return {
      mediaSlots: [
        { role: 'primary', required: true, prompt: 'Before photo' },
        { role: 'after', required: true, prompt: 'After photo' },
      ],
    };
  }
  if (kind === 'custom') {
    // Custom starts with 1 step
    return {
      mediaSlots: [
        { role: 'step_1', required: true, prompt: 'Step 1' },
      ],
    };
  }
  return {
    mediaSlots: [{ role: 'primary', required: true, prompt: 'Primary photo' }],
  };
}

interface LabelSchema {
  id: string;
  name: string;
  version: number;
  scope: 'detection';
  classes: any[];
  guidelines?: string;
  includeGlobalCategories?: boolean;
}

interface Task {
  id: string;
  title: string;
  description: string;
  type: string;
  category: string;
  subcategory: string;
  itemType: string;
  condition: string;
  view: string;
  reward: number;
  cooldownSeconds: number;
  maxSubmissionsPerCooldown?: number;
  icon: string;
  nextTaskId: string;
  bounty: boolean;
  active: boolean;
  oneTime?: boolean;
  mode: string;
  validationPrompt: string;
  addedDate: string;
  tags: string[];
  sampleImageUrl: string;
  sampleMedia?: SampleMedia;
  submissionSteps?: Partial<Record<StepKey, SubmissionStep>>;
  validationByRole?: Partial<Record<StepKey, any>>;
  expectedLabelsByRole?: Partial<Record<StepKey, string[]>>;
  classification?: {
    enabled: boolean;
    classId?: string;
    deriveFromFields?: boolean;
    includeInExports?: boolean;
  };
  detection?: {
    enabled: boolean;
    schemaRef?: { id: string; version: string };
    allowedCategories?: string[];
    slaHours?: number;
    priority?: number;
  };
  taskKind?: TaskKind;
  submissionSpec?: SubmissionSpec;
  visibility?: { languages: string[] };
  i18n?: { title?: Record<string, string>; description?: Record<string, string> };
  autoRules?: {
    enabled: boolean;
    modelId?: string;
    approveAt: number;
    rejectAt: number;
    auditRate: number;
    dailyCap: number;
    canaryPct: number;
    minConfidence?: number;
    updatedAt?: any;
    updatedBy?: string;
  };
}

const VISIBILITY_LANGUAGE_OPTIONS: { value: string; label: string }[] = [
  { value: "all", label: "All Languages" },
  { value: "en", label: "English" },
  { value: "vi", label: "Vietnamese" },
  { value: "es", label: "Spanish" },
  { value: "id", label: "Indonesian" },
  { value: "tl", label: "Filipino" },
  { value: "th", label: "Thai" },
  { value: "pt", label: "Portuguese" },
  { value: "hi", label: "Hindi" },
  { value: "de", label: "German" },
  { value: "fr", label: "French" },
  { value: "it", label: "Italian" },
];

const TRANSLATION_LANGUAGE_OPTIONS: { value: string; label: string }[] = [
  { value: "vi", label: "Vietnamese" },
  { value: "es", label: "Spanish" },
  { value: "id", label: "Bahasa Indonesia" },
  { value: "tl", label: "Filipino" },
  { value: "hi", label: "हिन्दी" },
  { value: "pt", label: "Português (Brasil)" },
  { value: "th", label: "ภาษาไทย" },
  { value: "de", label: "Deutsch" },
  { value: "fr", label: "Français" },
  { value: "it", label: "Italiano" },
];

const defaultTask: Task = {
  id: "",
  title: "",
  description: "",
  type: "Photo",
  category: "",
  subcategory: "",
  itemType: "",
  condition: "",
  view: "",
  reward: 0,
  cooldownSeconds: 0,
  maxSubmissionsPerCooldown: 1,
  icon: "",
  nextTaskId: "",
  bounty: false,
  active: true,
  oneTime: false,
  mode: "default",
  validationPrompt: "",
  addedDate: "",
  tags: [],
  sampleImageUrl: "",
  sampleMedia: {},
  submissionSteps: {},
  validationByRole: {},
  expectedLabelsByRole: {},
  visibility: { languages: ["all"] },
  classification: {
    enabled: true,
    classId: '',
    deriveFromFields: true,
    includeInExports: true
  },
  detection: {
    enabled: true,
    allowedCategories: [],
    slaHours: 24,
    priority: 3
  },
  taskKind: 'single',
  submissionSpec: buildDefaultSubmissionSpec('single'),
  i18n: {},
};

interface TaskModalProps {
  mode: 'create' | 'edit';
  taskId?: string;
  isOpen: boolean;
  onClose: () => void;
  onSave?: () => void;
}

export default function TaskModal({ mode, taskId, isOpen, onClose, onSave }: TaskModalProps) {
  const [activeTab, setActiveTab] = useState<'basic' | 'validation' | 'auto-approval' | 'steps' | 'translations'>('basic');
  const [task, setTask] = useState<Task>(defaultTask);
  const [cooldownDays, setCooldownDays] = useState<number>(0);
  const [categories, setCategories] = useState<any[]>([]);
  const [subcategories, setSubcategories] = useState<any[]>([]);
  const [itemTypes, setItemTypes] = useState<any[]>([]);
  const [schemas, setSchemas] = useState<LabelSchema[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [schemaSearchTerm, setSchemaSearchTerm] = useState('');
  const [showSchemaDropdown, setShowSchemaDropdown] = useState(false);
  const [allTasks, setAllTasks] = useState<Array<{id: string; title: string}>>([]);
  const [newTranslationLang, setNewTranslationLang] = useState('');
  const [newTranslationTitle, setNewTranslationTitle] = useState('');
  const [newTranslationDescription, setNewTranslationDescription] = useState('');
  const [editingTranslationLang, setEditingTranslationLang] = useState<string | null>(null);
  const [editTranslationTitle, setEditTranslationTitle] = useState('');
  const [editTranslationDescription, setEditTranslationDescription] = useState('');
  const [nextTaskSearchTerm, setNextTaskSearchTerm] = useState('');
  const [showNextTaskDropdown, setShowNextTaskDropdown] = useState(false);
  const [showImageCropModal, setShowImageCropModal] = useState(false);
  const [imageCropTarget, setImageCropTarget] = useState<'icon' | StepKey | null>(null);
  const [imageCropIndex, setImageCropIndex] = useState<number>(0);
  const [expandedAdvancedRoles, setExpandedAdvancedRoles] = useState<Set<StepKey>>(new Set());
  const [expectedLabelSearchTerms, setExpectedLabelSearchTerms] = useState<Record<StepKey, string>>({});
  const [showExpectedLabelDropdowns, setShowExpectedLabelDropdowns] = useState<Record<StepKey, boolean>>({});
  const expectedLabelDropdownRefs = useRef<Record<StepKey, HTMLDivElement | null>>({
    primary: null,
    expiration_label: null,
    after: null
  });
  const [videoUploading, setVideoUploading] = useState(false);
  const videoInputRef = useRef<HTMLInputElement | null>(null);
  
  const { categories: globalCategories } = useGlobalCategories();

  // Get unique categories by name (remove duplicates)
  const uniqueCategories = useMemo(() => {
    const seen = new Set<string>();
    return categories.filter(cat => {
      if (seen.has(cat.name)) {
        return false;
      }
      seen.add(cat.name);
      return true;
    });
  }, [categories]);

  // Filter subcategories by selected category (use ID if available, fallback to name)
  const filteredSubcategories = useMemo(() => {
    if (!task.category && !(task as any).categoryId) return [];
    
    // Find category by ID first, then by name (fallback)
    let selectedCategory: any = null;
    if ((task as any).categoryId) {
      selectedCategory = uniqueCategories.find(cat => cat.id === (task as any).categoryId);
    }
    if (!selectedCategory && task.category) {
      selectedCategory = uniqueCategories.find(cat => cat.name === task.category);
    }
    if (!selectedCategory) return [];
    
    return subcategories.filter(sub => sub.categoryId === selectedCategory.id);
  }, [task.category, (task as any).categoryId, uniqueCategories, subcategories]);

  // Helper function to normalize itemType names for comparison
  const normalizeItemTypeName = (name: string): string => {
    return (name || '').trim().toLowerCase();
  };

  // Filter item types by selected category + subcategory (use IDs if available, fallback to names)
  const filteredItemTypes = useMemo(() => {
    if ((!task.category && !(task as any).categoryId) || (!task.subcategory && !(task as any).subcategoryId)) {
      console.log("🔍 [TaskModal] ItemType filter: Missing category or subcategory", {
        category: task.category,
        categoryId: (task as any).categoryId,
        subcategory: task.subcategory,
        subcategoryId: (task as any).subcategoryId
      });
      return [];
    }
    
    // Find category by ID first, then by name (fallback)
    let selectedCategory: any = null;
    if ((task as any).categoryId) {
      selectedCategory = uniqueCategories.find(cat => cat.id === (task as any).categoryId);
    }
    if (!selectedCategory && task.category) {
      selectedCategory = uniqueCategories.find(cat => cat.name === task.category);
    }
    if (!selectedCategory) {
      console.warn("⚠️ [TaskModal] ItemType filter: Category not found", task.category, "categoryId:", (task as any).categoryId);
      return [];
    }
    
    // Find subcategory by ID first, then by name (fallback)
    let selectedSubcategory: any = null;
    if ((task as any).subcategoryId) {
      selectedSubcategory = subcategories.find(sub => sub.id === (task as any).subcategoryId);
    }
    if (!selectedSubcategory && task.subcategory) {
      selectedSubcategory = subcategories.find(sub => 
        sub.name === task.subcategory && sub.categoryId === selectedCategory.id
      );
    }
    if (!selectedSubcategory) {
      console.warn("⚠️ [TaskModal] ItemType filter: Subcategory not found", {
        subcategoryName: task.subcategory,
        subcategoryId: (task as any).subcategoryId,
        categoryId: selectedCategory.id,
        availableSubcategories: subcategories
          .filter(sub => sub.categoryId === selectedCategory.id)
          .map(sub => sub.name)
      });
      return [];
    }
    const filtered = itemTypes.filter(itemType => 
      itemType.categoryId === selectedCategory.id && 
      itemType.subcategoryId === selectedSubcategory.id &&
      itemType.active !== false
    );
    
    // Check if current task's itemType exists in ItemTypes collection (normalized comparison)
    const normalizedTaskItemType = task.itemType ? normalizeItemTypeName(task.itemType) : '';
    const currentItemTypeExists = itemTypes.some(it => normalizeItemTypeName(it.name) === normalizedTaskItemType);
    const currentItemTypeInList = filtered.some(it => normalizeItemTypeName(it.name) === normalizedTaskItemType);
    
    console.log("✅ [TaskModal] ItemType filter result:", {
      category: task.category,
      categoryId: selectedCategory.id,
      subcategory: task.subcategory,
      subcategoryId: selectedSubcategory.id,
      totalItemTypes: itemTypes.length,
      filteredCount: filtered.length,
      filteredNames: filtered.map(it => it.name),
      currentTaskItemType: task.itemType,
      normalizedTaskItemType: normalizedTaskItemType,
      currentItemTypeExists: currentItemTypeExists,
      currentItemTypeInList: currentItemTypeInList
    });
    
    // If itemType doesn't exist in collection, it will show as blank (handled in dropdown rendering)
    // Also clear itemType if it doesn't exist in collection
    if (task.itemType && normalizedTaskItemType && !currentItemTypeExists) {
      console.warn("⚠️ [TaskModal] Task itemType '", task.itemType, "' not found in ItemTypes collection. Clearing itemType.");
      // Note: This will be handled by the parent component or we can clear it here
      // For now, we'll let it show as blank in the dropdown
    }
    
    return filtered;
  }, [task.category, task.subcategory, task.itemType, uniqueCategories, subcategories, itemTypes]);

  const filteredSchemas = schemas.filter(schema => 
    schema.name.toLowerCase().includes(schemaSearchTerm.toLowerCase()) ||
    schema.guidelines?.toLowerCase().includes(schemaSearchTerm.toLowerCase())
  );

  const filteredNextTasks = allTasks.filter(task => 
    task.title.toLowerCase().includes(nextTaskSearchTerm.toLowerCase()) ||
    task.id.toLowerCase().includes(nextTaskSearchTerm.toLowerCase())
  );

  // Fetch data - using same logic as create-task.tsx
  useEffect(() => {
    if (!isOpen) return;

    const fetchData = async () => {
      setLoading(true);
      try {

        // Fetch schemas - same as create-task.tsx line 267-292
        const fetchSchemas = async () => {
          try {
            console.log("🔍 [TaskModal] Fetching active schemas...");
            // Try the query with both conditions
            const activeSchemasQuery = query(
              collection(db, 'labelSchemas'),
              where('archived', '!=', true),
              where('scope', '==', 'detection')
            );
            const activeSchemasSnapshot = await getDocs(activeSchemasQuery);
            console.log("📊 [TaskModal] Active schemas found:", activeSchemasSnapshot.docs.length);
            
            const schemasData = activeSchemasSnapshot.docs.map(doc => {
              const data = doc.data();
              console.log("📋 [TaskModal] Active schema:", doc.id, data);
              return {
                id: doc.id,
                ...data
              };
            }) as LabelSchema[];
            
            console.log("✅ [TaskModal] Setting active schemas:", schemasData);
            setSchemas(schemasData);
          } catch (error: any) {
            // Check if it's an index error
            if (error?.code === 'failed-precondition' && error?.message?.includes('index')) {
              console.warn("⚠️ [TaskModal] Firestore index required. Using fallback query (this is normal until index is created)...");
            } else {
              console.error("❌ [TaskModal] Error fetching schemas:", error);
            }
            // Fallback: try fetching all schemas without filters
            try {
              console.log("🔄 [TaskModal] Trying fallback: fetch all schemas...");
              const allSchemasSnap = await getDocs(collection(db, 'labelSchemas'));
              const allSchemasData = allSchemasSnap.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter((s: any) => !s.archived && s.scope === 'detection') as LabelSchema[];
              console.log("📊 [TaskModal] Fallback schemas found:", allSchemasData.length);
              setSchemas(allSchemasData);
            } catch (fallbackError) {
              console.error("❌ [TaskModal] Fallback also failed:", fallbackError);
              setSchemas([]);
            }
          }
        };

        // Fetch categories and subcategories first (needed for itemTypes)
        const [categoriesResult, subcategoriesResult] = await Promise.all([
          getDocs(collection(db, "categories")),
          getDocs(collection(db, "subcategories"))
        ]);
        
        const categoriesData = categoriesResult.docs.map(doc => ({
          ...doc.data(),
          id: doc.id
        }));
        const subcategoriesData = subcategoriesResult.docs.map(doc => ({
          ...doc.data(),
          id: doc.id
        }));
        
        setCategories(categoriesData);
        setSubcategories(subcategoriesData);

        // Fetch itemTypes using the fetched categories/subcategories
        const fetchItemTypesWithData = async (cats: any[], subs: any[]) => {
          try {
            let itemTypesData: any[] = [];
            try {
              const itemTypesSnap = await getDocs(collection(db, "itemTypes"));
              itemTypesData = itemTypesSnap.docs.map(doc => ({
                ...doc.data(),
                id: doc.id
              }));
            } catch (collectionError: any) {
              console.log("ItemTypes collection not accessible, using tasks only:", collectionError.message);
            }

            const tasksSnap = await getDocs(collection(db, "tasks"));
            const itemTypeToTaskMap = new Map<string, {name: string, category: string, subcategory: string}>();
            tasksSnap.docs.forEach(doc => {
              const task = doc.data();
              if (task.itemType && typeof task.itemType === 'string' && task.itemType.trim()) {
                const itemTypeName = task.itemType.trim();
                if (!itemTypeToTaskMap.has(itemTypeName) && 
                    task.category && typeof task.category === 'string' && task.category.trim() &&
                    task.subcategory && typeof task.subcategory === 'string' && task.subcategory.trim()) {
                  itemTypeToTaskMap.set(itemTypeName, {
                    name: itemTypeName,
                    category: task.category.trim(),
                    subcategory: task.subcategory.trim()
                  });
                }
              }
            });

            const itemTypesMap = new Map<string, any>();
            itemTypesData.forEach((it: any) => {
              if (it.name) {
                itemTypesMap.set(it.name, it);
              }
            });

            const tempItemTypes: any[] = [];
            itemTypeToTaskMap.forEach((taskInfo, name) => {
              if (name && !itemTypesMap.has(name)) {
                const category = cats.find((cat: any) => {
                  const catName = (cat.name || '').trim();
                  const taskCatName = (taskInfo.category || '').trim();
                  return catName === taskCatName || catName.toLowerCase() === taskCatName.toLowerCase();
                });
                
                let subcategory = null;
                if (category && taskInfo.subcategory) {
                  const taskSubName = (taskInfo.subcategory || '').trim();
                  subcategory = subs.find((sub: any) => {
                    const subName = (sub.name || '').trim();
                    const nameMatches = subName === taskSubName || subName.toLowerCase() === taskSubName.toLowerCase();
                    const belongsToCategory = sub.categoryId === category.id;
                    return nameMatches && belongsToCategory;
                  });
                }
                
                tempItemTypes.push({
                  id: `temp-${name}`,
                  name,
                  description: "",
                  categoryId: category?.id || "",
                  subcategoryId: subcategory?.id || "",
                  active: true,
                  addedDate: new Date().toISOString(),
                  fromTasks: true
                });
              }
            });

            const allItemTypes = [...itemTypesData, ...tempItemTypes].sort((a, b) => 
              (a.name || '').localeCompare(b.name || '')
            );

            console.log("✅ [TaskModal] ItemTypes loaded:", {
              fromCollection: itemTypesData.length,
              fromTasks: tempItemTypes.length,
              total: allItemTypes.length,
              details: allItemTypes.map(it => ({
                id: it.id,
                name: it.name,
                categoryId: it.categoryId,
                subcategoryId: it.subcategoryId,
                active: it.active,
                fromTasks: it.fromTasks
              }))
            });

            setItemTypes(allItemTypes);
          } catch (error) {
            console.error("Error fetching item types:", error);
            setItemTypes([]);
          }
        };

        // Fetch all tasks for Next Task dropdown
        const fetchAllTasks = async () => {
          try {
            const tasksSnap = await getDocs(collection(db, "tasks"));
            const tasksList = tasksSnap.docs.map(doc => ({
              id: doc.id,
              title: doc.data().title || 'Untitled Task'
            }));
            setAllTasks(tasksList);
          } catch (error) {
            console.error("Error fetching tasks:", error);
            setAllTasks([]);
          }
        };

        await Promise.all([
          fetchItemTypesWithData(categoriesData, subcategoriesData),
          fetchSchemas(),
          fetchAllTasks()
        ]);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isOpen]);

  // Load task for editing
  useEffect(() => {
    if (!isOpen) return;

    if (mode === 'edit' && taskId) {
      setLoading(true);
      getDoc(doc(db, "tasks", taskId)).then(docSnap => {
        if (docSnap.exists()) {
          const firestoreData = docSnap.data();
          console.log("📥 [TaskModal] Loading task from Firestore:", taskId);
          console.log("📋 [TaskModal] Raw Firestore data:", firestoreData);
          
          // Log all validation-related keys to identify legacy structures
          const allKeys = Object.keys(firestoreData);
          const validationKeys = allKeys.filter(key => 
            key.toLowerCase().includes('validation') || 
            key.toLowerCase().includes('expected') ||
            key.toLowerCase().includes('label') ||
            key.toLowerCase().includes('required') ||
            key.toLowerCase().includes('caption') ||
            key.toLowerCase().includes('confidence')
          );
          console.log("🔑 [TaskModal] All validation-related keys found:", validationKeys);
          
          // Log the actual values of validation-related keys for debugging
          validationKeys.forEach(key => {
            console.log(`📝 [TaskModal] ${key}:`, firestoreData[key]);
          });
          console.log("🔍 [TaskModal] validationPrompt:", firestoreData.validationPrompt);
          console.log("🔍 [TaskModal] validationByRole:", firestoreData.validationByRole);
          console.log("🔍 [TaskModal] expectedLabelsByRole:", firestoreData.expectedLabelsByRole);
          
          // Legacy fallback: Check for task-level validation fields and map to 'primary' role
          const migrateLegacyValidation = (data: any) => {
            let validationByRole = (data.validationByRole && typeof data.validationByRole === 'object') 
              ? { ...data.validationByRole } 
              : {};
            let expectedLabelsByRole = (data.expectedLabelsByRole && typeof data.expectedLabelsByRole === 'object')
              ? { ...data.expectedLabelsByRole }
              : {};
            
            // Check for legacy flat validation fields and map to 'primary' role
            const legacyMappings: { [key: string]: { role: MediaRole; field: string } } = {
              'expectedLabels': { role: 'primary', field: 'expectedLabels' },
              'requiredObjects': { role: 'primary', field: 'requiredObjects' },
              'captionKeywords': { role: 'primary', field: 'captionKeywords' },
              'minConfidence': { role: 'primary', field: 'minConfidence' },
              'customValidation': { role: 'primary', field: 'customValidation' },
              // Check for step1/step2 variants
              'step1ExpectedLabels': { role: 'primary', field: 'expectedLabels' },
              'step2ExpectedLabels': { role: 'expiration_label', field: 'expectedLabels' },
              'step1RequiredObjects': { role: 'primary', field: 'requiredObjects' },
              'step2RequiredObjects': { role: 'expiration_label', field: 'requiredObjects' },
            };
            
            // Check for alternative role keys (step1, step2, etc.)
            const roleKeyMappings: { [key: string]: MediaRole } = {
              'step1': 'primary',
              'step2': 'expiration_label', // Default to expiration_label, will be adjusted for before_after
              'primary': 'primary',
              'expiration_label': 'expiration_label',
              'expirationLabel': 'expiration_label',
              'after': 'after',
            };
            
            // Determine task kind to know which roles to check
            const taskKind = (data.taskKind || data.kind || 'single') as TaskKind;
            if (taskKind === 'before_after') {
              roleKeyMappings['step2'] = 'after';
            }
            
            // Check if validationByRole exists but uses alternative role keys
            if (data.validationByRole && typeof data.validationByRole === 'object') {
              Object.keys(data.validationByRole).forEach(roleKey => {
                const mappedRole = roleKeyMappings[roleKey.toLowerCase()];
                if (mappedRole && mappedRole !== roleKey) {
                  console.log(`🔄 [TaskModal] Migrating validationByRole key: ${roleKey} -> ${mappedRole}`);
                  if (!validationByRole[mappedRole]) {
                    validationByRole[mappedRole] = {};
                  }
                  validationByRole[mappedRole] = {
                    ...validationByRole[mappedRole],
                    ...data.validationByRole[roleKey]
                  };
                }
              });
            }
            
            // Check if expectedLabelsByRole exists but uses alternative role keys
            if (data.expectedLabelsByRole && typeof data.expectedLabelsByRole === 'object') {
              Object.keys(data.expectedLabelsByRole).forEach(roleKey => {
                const mappedRole = roleKeyMappings[roleKey.toLowerCase()];
                if (mappedRole && mappedRole !== roleKey) {
                  console.log(`🔄 [TaskModal] Migrating expectedLabelsByRole key: ${roleKey} -> ${mappedRole}`);
                  expectedLabelsByRole[mappedRole] = data.expectedLabelsByRole[roleKey];
                }
              });
            }
            
            // Map legacy flat fields to 'primary' role
            Object.keys(legacyMappings).forEach(legacyKey => {
              if (data[legacyKey] !== undefined && data[legacyKey] !== null) {
                const mapping = legacyMappings[legacyKey];
                console.log(`🔄 [TaskModal] Migrating legacy field: ${legacyKey} -> validationByRole[${mapping.role}].${mapping.field}`);
                
                if (mapping.field === 'expectedLabels') {
                  // Handle expectedLabels specially (it goes to expectedLabelsByRole, not validationByRole)
                  if (!expectedLabelsByRole[mapping.role] || expectedLabelsByRole[mapping.role].length === 0) {
                    expectedLabelsByRole[mapping.role] = Array.isArray(data[legacyKey]) 
                      ? data[legacyKey] 
                      : typeof data[legacyKey] === 'string'
                      ? data[legacyKey].split('\n').map((s: string) => s.trim()).filter(Boolean)
                      : [];
                  }
                } else {
                  // Other fields go to validationByRole
                  if (!validationByRole[mapping.role]) {
                    validationByRole[mapping.role] = {};
                  }
                  // Only set if not already present (preserve existing role-based data)
                  if (validationByRole[mapping.role][mapping.field] === undefined) {
                    validationByRole[mapping.role][mapping.field] = data[legacyKey];
                  }
                }
              }
            });
            
            // Deep search: Check for validation data in nested objects (e.g., validationDetails, validationRules, etc.)
            const deepSearchKeys = ['validationDetails', 'validationRules', 'rules', 'validation'];
            deepSearchKeys.forEach(nestedKey => {
              if (data[nestedKey] && typeof data[nestedKey] === 'object') {
                console.log(`🔍 [TaskModal] Found nested validation object: ${nestedKey}`, data[nestedKey]);
                const nested = data[nestedKey];
                
                // Check for expectedLabels in nested object (this is what workbench/review use)
                if (nested.expectedLabels) {
                  const labelsArray = Array.isArray(nested.expectedLabels)
                    ? nested.expectedLabels
                    : typeof nested.expectedLabels === 'string'
                    ? nested.expectedLabels.split('\n').map((s: string) => s.trim()).filter(Boolean)
                    : [];
                  
                  // If expectedLabelsByRole.primary doesn't exist or is empty, migrate from validationRules
                  if (!expectedLabelsByRole['primary'] || expectedLabelsByRole['primary'].length === 0) {
                    expectedLabelsByRole['primary'] = labelsArray;
                    console.log(`🔄 [TaskModal] Migrated ${nestedKey}.expectedLabels to expectedLabelsByRole.primary:`, labelsArray);
                  } else {
                    // Merge if both exist (avoid duplicates)
                    const existing = new Set(expectedLabelsByRole['primary']);
                    labelsArray.forEach((label: string) => existing.add(label));
                    expectedLabelsByRole['primary'] = Array.from(existing);
                    console.log(`🔄 [TaskModal] Merged ${nestedKey}.expectedLabels with existing expectedLabelsByRole.primary`);
                  }
                }
                
                // Check for expectedLabelCategories (used in Review page)
                if (nested.expectedLabelCategories && Array.isArray(nested.expectedLabelCategories)) {
                  // Store in validationByRole.primary for consistency
                  if (!validationByRole['primary']) {
                    validationByRole['primary'] = {};
                  }
                  if (!validationByRole['primary'].expectedLabelCategories) {
                    validationByRole['primary'].expectedLabelCategories = nested.expectedLabelCategories;
                    console.log(`🔄 [TaskModal] Migrated ${nestedKey}.expectedLabelCategories to validationByRole.primary.expectedLabelCategories`);
                  }
                }
                
                // Check for validation fields in nested object
                ['requiredObjects', 'captionKeywords', 'minConfidence'].forEach(field => {
                  if (nested[field] !== undefined && nested[field] !== null) {
                    if (!validationByRole['primary']) {
                      validationByRole['primary'] = {};
                    }
                    if (validationByRole['primary'][field] === undefined) {
                      validationByRole['primary'][field] = nested[field];
                      console.log(`🔄 [TaskModal] Migrated ${nestedKey}.${field} to validationByRole.primary.${field}`);
                    }
                  }
                });
                
                // Handle customValidation specially - convert objects to JSON strings
                if (nested.customValidation !== undefined && nested.customValidation !== null) {
                  if (!validationByRole['primary']) {
                    validationByRole['primary'] = {};
                  }
                  if (validationByRole['primary'].customValidation === undefined) {
                    // If it's an object, stringify it; if it's already a string, use it as-is
                    if (typeof nested.customValidation === 'object') {
                      validationByRole['primary'].customValidation = JSON.stringify(nested.customValidation, null, 2);
                      console.log(`🔄 [TaskModal] Migrated and stringified ${nestedKey}.customValidation (object) to validationByRole.primary.customValidation`);
                    } else {
                      validationByRole['primary'].customValidation = nested.customValidation;
                      console.log(`🔄 [TaskModal] Migrated ${nestedKey}.customValidation to validationByRole.primary.customValidation`);
                    }
                  }
                }
              }
            });
            
            return { validationByRole, expectedLabelsByRole };
          };
          
          const { validationByRole, expectedLabelsByRole } = migrateLegacyValidation(firestoreData);
          
          // Convert any customValidation objects to JSON strings in validationByRole
          Object.keys(validationByRole).forEach(role => {
            const roleValidation = validationByRole[role as MediaRole];
            if (roleValidation && typeof roleValidation === 'object' && roleValidation.customValidation) {
              if (typeof roleValidation.customValidation === 'object') {
                roleValidation.customValidation = JSON.stringify(roleValidation.customValidation, null, 2);
                console.log(`🔄 [TaskModal] Converted customValidation object to JSON string for role ${role}`);
              }
            }
          });
          
          // Properly merge nested objects - ensure validationByRole and expectedLabelsByRole are preserved
          // Handle both undefined and null cases, and ensure objects are properly structured
          const loadedTask = {
            ...defaultTask,
            ...firestoreData,
            id: taskId,
            // Use migrated validation data
            validationByRole,
            expectedLabelsByRole,
            validationPrompt: firestoreData.validationPrompt ?? '',
            // Set maxSubmissionsPerCooldown with default of 1 if not present
            maxSubmissionsPerCooldown: firestoreData.maxSubmissionsPerCooldown ?? 1,
            // Preserve visibility / language
            visibility: (firestoreData.visibility && Array.isArray(firestoreData.visibility.languages) && firestoreData.visibility.languages.length > 0)
              ? { languages: firestoreData.visibility.languages }
              : { languages: ['all'] },
            // Preserve other nested objects
            sampleMedia: (firestoreData.sampleMedia && typeof firestoreData.sampleMedia === 'object') 
              ? firestoreData.sampleMedia 
              : {},
            submissionSteps: (firestoreData.submissionSteps && typeof firestoreData.submissionSteps === 'object')
              ? firestoreData.submissionSteps
              : {},
            submissionSpec: firestoreData.submissionSpec || buildDefaultSubmissionSpec((firestoreData.taskKind || firestoreData.kind || 'single') as TaskKind),
            // Handle taskKind - check both 'taskKind' and 'kind' for backward compatibility
            taskKind: (() => {
              const kind = (firestoreData.taskKind || firestoreData.kind || 'single') as TaskKind;
              console.log('📥 [TaskModal] Loaded taskKind from Firebase:', {
                taskKind: firestoreData.taskKind,
                kind: firestoreData.kind,
                resolved: kind
              });
              return kind;
            })(),
            i18n: (firestoreData.i18n && typeof firestoreData.i18n === 'object') ? firestoreData.i18n : {},
          };
          
          console.log("✅ [TaskModal] Loaded task with validation data:", {
            validationPrompt: loadedTask.validationPrompt,
            validationByRole: loadedTask.validationByRole,
            expectedLabelsByRole: loadedTask.expectedLabelsByRole,
          });
          console.log("🔍 [TaskModal] Detailed validationByRole:", JSON.stringify(loadedTask.validationByRole, null, 2));
          console.log("🔍 [TaskModal] Detailed expectedLabelsByRole:", JSON.stringify(loadedTask.expectedLabelsByRole, null, 2));
          
          setTask(loadedTask);
          // Convert cooldownSeconds to days for display
          const days = loadedTask.cooldownSeconds / (24 * 60 * 60);
          setCooldownDays(days);
        }
        setLoading(false);
      }).catch(error => {
        console.error("❌ [TaskModal] Error loading task:", error);
        setLoading(false);
      });
    } else {
      setTask(defaultTask);
      setCooldownDays(0);
      setActiveTab('basic');
    }
  }, [mode, taskId, isOpen]);

  // Populate category/subcategory names from IDs if missing (after categories/subcategories are loaded)
  useEffect(() => {
    if (task.id && categories.length > 0 && subcategories.length > 0) {
      const updatedTask = { ...task };
      let needsUpdate = false;
      
      // If task has categoryId but not category name, populate name
      if ((task as any).categoryId && !task.category) {
        const cat = categories.find(c => c.id === (task as any).categoryId);
        if (cat) {
          updatedTask.category = cat.name;
          needsUpdate = true;
        }
      }
      
      // If task has subcategoryId but not subcategory name, populate name
      if ((task as any).subcategoryId && !task.subcategory) {
        const sub = subcategories.find(s => s.id === (task as any).subcategoryId);
        if (sub) {
          updatedTask.subcategory = sub.name;
          needsUpdate = true;
        }
      }
      
      if (needsUpdate) {
        console.log("🔄 [TaskModal] Populated category/subcategory names from IDs");
        setTask(updatedTask);
      }
    }
  }, [task.id, (task as any).categoryId, (task as any).subcategoryId, categories, subcategories]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.schema-dropdown')) {
        setShowSchemaDropdown(false);
      }
      if (!target.closest('.next-task-dropdown')) {
        setShowNextTaskDropdown(false);
      }
    };

    if (showSchemaDropdown || showNextTaskDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSchemaDropdown, showNextTaskDropdown]);

  // Close expected label dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      Object.keys(expectedLabelDropdownRefs.current).forEach(stepKey => {
        const ref = expectedLabelDropdownRefs.current[stepKey as StepKey];
        if (ref && !ref.contains(target)) {
          setShowExpectedLabelDropdowns(prev => ({ ...prev, [stepKey]: false }));
        }
      });
    };

    const hasOpenDropdown = Object.values(showExpectedLabelDropdowns).some(Boolean);
    if (hasOpenDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showExpectedLabelDropdowns]);

  const handleTaskChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    if (name === 'taskKind') {
      const kind = (value || 'single') as TaskKind;
      console.log('🔄 [TaskModal] Task Kind changed to:', kind);
      setTask((prev: any) => ({
        ...prev,
        taskKind: kind,
        submissionSpec: buildDefaultSubmissionSpec(kind),
      }));
      return;
    }

    // Handle category change - set both name and ID
    if (name === 'category') {
      const selectedCategory = uniqueCategories.find(cat => cat.name === value);
      setTask((prev: any) => ({ 
        ...prev, 
        category: value,
        categoryId: selectedCategory ? selectedCategory.id : '',
        subcategory: '',
        subcategoryId: '',
        itemType: ''
      }));
      return;
    }

    // Handle subcategory change - set both name and ID
    if (name === 'subcategory') {
      const selectedCategory = uniqueCategories.find(cat => 
        cat.name === task.category || cat.id === (task as any).categoryId
      );
      const selectedSubcategory = selectedCategory 
        ? subcategories.find(sub => sub.name === value && sub.categoryId === selectedCategory.id)
        : null;
      setTask((prev: any) => ({ 
        ...prev, 
        subcategory: value,
        subcategoryId: selectedSubcategory ? selectedSubcategory.id : '',
        itemType: ''
      }));
      return;
    }

    if (name === 'visibilityLanguage') {
      setTask((prev: any) => ({
        ...prev,
        visibility: { languages: [value] },
      }));
      return;
    }

    setTask((prev: any) => ({ 
      ...prev, 
      [name]: type === "checkbox" ? checked : value 
    }));
  };

  const addTranslation = () => {
    if (!newTranslationLang.trim()) return;
    setTask((prev: any) => {
      const prevI18n = prev.i18n || {};
      const prevTitle = prevI18n.title || {};
      const prevDescription = prevI18n.description || {};
      return {
        ...prev,
        i18n: {
          title: { ...prevTitle, [newTranslationLang]: newTranslationTitle },
          description: { ...prevDescription, [newTranslationLang]: newTranslationDescription },
        },
      };
    });
    setNewTranslationLang('');
    setNewTranslationTitle('');
    setNewTranslationDescription('');
  };

  const updateTranslation = (lang: string) => {
    setTask((prev: any) => {
      const prevI18n = prev.i18n || {};
      return {
        ...prev,
        i18n: {
          title: { ...(prevI18n.title || {}), [lang]: editTranslationTitle },
          description: { ...(prevI18n.description || {}), [lang]: editTranslationDescription },
        },
      };
    });
    setEditingTranslationLang(null);
    setEditTranslationTitle('');
    setEditTranslationDescription('');
  };

  const deleteTranslation = (lang: string) => {
    setTask((prev: any) => {
      const prevI18n = prev.i18n || {};
      const newTitle = { ...(prevI18n.title || {}) };
      const newDescription = { ...(prevI18n.description || {}) };
      delete newTitle[lang];
      delete newDescription[lang];
      return {
        ...prev,
        i18n: { title: newTitle, description: newDescription },
      };
    });
    if (editingTranslationLang === lang) {
      setEditingTranslationLang(null);
      setEditTranslationTitle('');
      setEditTranslationDescription('');
    }
  };

  const setSampleMediaAt = (stepKey: StepKey, index: number, url: string) => {
    setTask((prev: any) => {
      const prevList: string[] = Array.isArray(prev.sampleMedia?.[stepKey]) ? prev.sampleMedia[stepKey] : [];
      const nextList = [...prevList];
      // Only ensure we have at least index + 1 elements (only need index 0 now)
      while (nextList.length <= index) nextList.push('');
      nextList[index] = url;
      const step = prev.submissionSteps?.[stepKey] || { title: '', description: '', examples: [] };
      // Filter out empty strings to keep the array clean
      const examplesList = nextList.filter(Boolean);
      return { 
        ...prev, 
        sampleMedia: { ...(prev.sampleMedia || {}), [stepKey]: nextList },
        submissionSteps: { 
          ...(prev.submissionSteps || {}), 
          [stepKey]: { ...step, examples: examplesList }
        }
      };
    });
  };

  const setSubmissionStepField = (stepKey: StepKey, field: 'title' | 'description', value: string) => {
    setTask((prev: any) => {
      const step = prev.submissionSteps?.[stepKey] || { title: '', description: '', examples: [] };
      if (step.examples.length === 0 && prev.sampleMedia?.[stepKey]) {
        step.examples = prev.sampleMedia[stepKey].filter(Boolean);
      }
      return {
        ...prev,
        submissionSteps: {
          ...(prev.submissionSteps || {}),
          [stepKey]: { ...step, [field]: value }
        }
      };
    });
  };

  // Custom step management functions
  const addCustomStep = () => {
    const stepSlots = getTaskStepSlots(task);
    const existingStepNumbers = stepSlots
      .map(slot => {
        const match = slot.role.match(/^step_(\d+)$/);
        return match ? parseInt(match[1], 10) : 0;
      })
      .filter(n => n > 0);
    
    const nextNumber = existingStepNumbers.length > 0 
      ? Math.max(...existingStepNumbers) + 1 
      : stepSlots.length + 1;
    
    const newStepKey = `step_${nextNumber}`;
    const newSlot = {
      role: newStepKey,
      prompt: `Step ${nextNumber}`,
      required: true
    };
    
    setTask((prev: any) => {
      const currentSlots = prev.submissionSpec?.mediaSlots || [];
      return {
        ...prev,
        submissionSpec: {
          ...prev.submissionSpec,
          mediaSlots: [...currentSlots, newSlot]
        },
        // Initialize empty objects for new step
        submissionSteps: {
          ...(prev.submissionSteps || {}),
          [newStepKey]: { title: '', description: '', examples: [] }
        },
        validationByRole: {
          ...(prev.validationByRole || {}),
          [newStepKey]: {}
        },
        expectedLabelsByRole: {
          ...(prev.expectedLabelsByRole || {}),
          [newStepKey]: []
        }
      };
    });
  };

  const removeCustomStep = (stepKey: StepKey) => {
    setTask((prev: any) => {
      const currentSlots = prev.submissionSpec?.mediaSlots || [];
      const newSlots = currentSlots.filter((slot: any) => slot.role !== stepKey);
      
      // Remove step from all related maps
      const newSubmissionSteps = { ...prev.submissionSteps };
      delete newSubmissionSteps[stepKey];
      
      const newValidationByRole = { ...prev.validationByRole };
      delete newValidationByRole[stepKey];
      
      const newExpectedLabelsByRole = { ...prev.expectedLabelsByRole };
      delete newExpectedLabelsByRole[stepKey];
      
      const newSampleMedia = { ...prev.sampleMedia };
      delete newSampleMedia[stepKey];
      
      return {
        ...prev,
        submissionSpec: {
          ...prev.submissionSpec,
          mediaSlots: newSlots
        },
        submissionSteps: newSubmissionSteps,
        validationByRole: newValidationByRole,
        expectedLabelsByRole: newExpectedLabelsByRole,
        sampleMedia: newSampleMedia
      };
    });
  };

  const updateCustomStepSlot = (stepKey: StepKey, field: 'prompt' | 'required', value: string | boolean) => {
    setTask((prev: any) => {
      const currentSlots = prev.submissionSpec?.mediaSlots || [];
      const newSlots = currentSlots.map((slot: any) => 
        slot.role === stepKey 
          ? { ...slot, [field]: value }
          : slot
      );
      
      return {
        ...prev,
        submissionSpec: {
          ...prev.submissionSpec,
          mediaSlots: newSlots
        }
      };
    });
  };

  const setExpectedLabelsByRoleFromText = (stepKey: StepKey, text: string) => {
    const labels = text.split('\n').map(s => s.trim()).filter(Boolean);
    setTask((prev: any) => ({
      ...prev,
      expectedLabelsByRole: { ...(prev.expectedLabelsByRole || {}), [stepKey]: labels }
    }));
  };

  // Get available labels from schema classes and global categories
  const getAvailableLabels = useMemo(() => {
    const availableLabels: Array<{ id: string; display: string; source: 'schema' | 'global' }> = [];
    
    // Get labels from selected detection schema
    const selectedSchema = schemas.find(s => s.id === task.detection?.schemaRef?.id);
    if (selectedSchema?.classes) {
      selectedSchema.classes.forEach((cls: any) => {
        const display = cls.displayName || cls.label || cls.name || cls.id;
        availableLabels.push({
          id: cls.id,
          display: display,
          source: 'schema'
        });
      });
    }
    
    // Get labels from global categories
    globalCategories.forEach(cat => {
      const display = cat.displayName || cat.name || cat.id;
      availableLabels.push({
        id: cat.id,
        display: display,
        source: 'global'
      });
    });
    
    // Remove duplicates (by id)
    const uniqueLabels = new Map<string, { id: string; display: string; source: 'schema' | 'global' }>();
    availableLabels.forEach(label => {
      if (!uniqueLabels.has(label.id)) {
        uniqueLabels.set(label.id, label);
      }
    });
    
    return Array.from(uniqueLabels.values()).sort((a, b) => a.display.localeCompare(b.display));
  }, [schemas, task.detection?.schemaRef?.id, globalCategories]);

  // Filter available labels based on search term for a specific step
  const getFilteredLabels = (stepKey: StepKey) => {
    const searchTerm = (expectedLabelSearchTerms[stepKey] || '').toLowerCase();
    if (!searchTerm) return getAvailableLabels.slice(0, 50); // Limit to 50 when no search
    
    return getAvailableLabels.filter(label => 
      label.id.toLowerCase().includes(searchTerm) ||
      label.display.toLowerCase().includes(searchTerm)
    ).slice(0, 50);
  };

  // Add a label to expectedLabelsByRole
  const addExpectedLabel = (stepKey: StepKey, labelId: string) => {
    const currentLabels = (task.expectedLabelsByRole || {})[stepKey] || [];
    if (!currentLabels.includes(labelId)) {
      setTask((prev: any) => ({
        ...prev,
        expectedLabelsByRole: {
          ...(prev.expectedLabelsByRole || {}),
          [stepKey]: [...currentLabels, labelId]
        }
      }));
    }
    setExpectedLabelSearchTerms(prev => ({ ...prev, [stepKey]: '' }));
    setShowExpectedLabelDropdowns(prev => ({ ...prev, [stepKey]: false }));
  };

  // Remove a label from expectedLabelsByRole
  const removeExpectedLabel = (stepKey: StepKey, labelId: string) => {
    const currentLabels = (task.expectedLabelsByRole || {})[stepKey] || [];
    setTask((prev: any) => ({
      ...prev,
      expectedLabelsByRole: {
        ...(prev.expectedLabelsByRole || {}),
        [stepKey]: currentLabels.filter((id: string) => id !== labelId)
      }
    }));
  };

  // Get display name for a label ID
  const getLabelDisplay = (labelId: string): string => {
    const label = getAvailableLabels.find(l => l.id === labelId);
    if (label) return label.display;
    
    // Fallback: try to find in schema classes
    const selectedSchema = schemas.find(s => s.id === task.detection?.schemaRef?.id);
    if (selectedSchema?.classes) {
      const cls = selectedSchema.classes.find((c: any) => c.id === labelId);
      if (cls) return cls.displayName || cls.label || cls.name || cls.id;
    }
    
    // Fallback: try global categories
    const globalCat = globalCategories.find(c => c.id === labelId);
    if (globalCat) return globalCat.displayName || globalCat.name || globalCat.id;
    
    return labelId; // Last resort: return the ID
  };

  const setValidationByRoleField = (stepKey: StepKey, field: string, value: any) => {
    setTask((prev: any) => ({
      ...prev,
      validationByRole: {
        ...(prev.validationByRole || {}),
        [stepKey]: { ...((prev.validationByRole || {})[stepKey] || {}), [field]: value }
      }
    }));
  };

  const validateJSON = (jsonString: string): boolean => {
    if (!jsonString || jsonString.trim() === '') return true;
    try {
      JSON.parse(jsonString);
      return true;
    } catch {
      return false;
    }
  };

  const toggleAdvancedRole = (stepKey: StepKey) => {
    setExpandedAdvancedRoles(prev => {
      const next = new Set(prev);
      if (next.has(stepKey)) {
        next.delete(stepKey);
      } else {
        next.add(stepKey);
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    setSaving(true);

    try {
      const id = taskId || task.id || Date.now().toString();
      
      // Sync expectedLabelsByRole to validationRules.expectedLabels for workbench/review compatibility
      // Workbench and Review components read from task.validationRules.expectedLabels
      const stepSlots = getTaskStepSlots(task);
      const stepKeys = stepSlots.map(slot => slot.role);
      const primaryExpectedLabels = task.expectedLabelsByRole?.primary || [];
      const allExpectedLabels = stepKeys.reduce((acc: string[], stepKey: StepKey) => {
        const labels = task.expectedLabelsByRole?.[stepKey] || [];
        return [...acc, ...labels];
      }, []);
      
      // For single tasks, use primary. For multi-photo, combine all step keys
      const expectedLabelsForValidationRules = task.taskKind === 'single' 
        ? primaryExpectedLabels 
        : allExpectedLabels;
      
      // Build validationRules object from role-based data
      const validationRules: any = {};
      
      // Set expectedLabels from expectedLabelsByRole.primary (or all roles for multi-photo)
      if (expectedLabelsForValidationRules.length > 0) {
        validationRules.expectedLabels = expectedLabelsForValidationRules;
      }
      
      // Sync other validation fields from primary role to validationRules (for backward compatibility)
      const primaryValidation = task.validationByRole?.primary || {};
      if (primaryValidation.requiredObjects && Array.isArray(primaryValidation.requiredObjects) && primaryValidation.requiredObjects.length > 0) {
        validationRules.requiredObjects = primaryValidation.requiredObjects;
      }
      if (primaryValidation.captionKeywords && Array.isArray(primaryValidation.captionKeywords) && primaryValidation.captionKeywords.length > 0) {
        validationRules.captionKeywords = primaryValidation.captionKeywords;
      }
      if (primaryValidation.minConfidence !== undefined && primaryValidation.minConfidence !== null) {
        validationRules.minConfidence = primaryValidation.minConfidence;
      }
      if (primaryValidation.customValidation) {
        validationRules.customValidation = primaryValidation.customValidation;
      }
      // Preserve expectedLabelCategories if it exists (used in Review page)
      if (primaryValidation.expectedLabelCategories && Array.isArray(primaryValidation.expectedLabelCategories) && primaryValidation.expectedLabelCategories.length > 0) {
        validationRules.expectedLabelCategories = primaryValidation.expectedLabelCategories;
      }
      
      // Clean up undefined values and ensure proper structure
      const cleanTask = { ...task };
      
      // Remove undefined values (Firestore doesn't accept undefined)
      Object.keys(cleanTask).forEach(key => {
        if (cleanTask[key as keyof typeof cleanTask] === undefined) {
          delete cleanTask[key as keyof typeof cleanTask];
        }
      });
      
      // Ensure submissionSpec.mediaSlots is properly structured for custom tasks
      if (cleanTask.taskKind === 'custom') {
        if (!cleanTask.submissionSpec) {
          cleanTask.submissionSpec = buildDefaultSubmissionSpec('custom');
        }
        if (!cleanTask.submissionSpec.mediaSlots || !Array.isArray(cleanTask.submissionSpec.mediaSlots) || cleanTask.submissionSpec.mediaSlots.length === 0) {
          cleanTask.submissionSpec.mediaSlots = [{ role: 'step_1', prompt: 'Step 1', required: true }];
        }
      }
      
      // Ensure categoryId and subcategoryId are set when saving
      let finalCategoryId = (cleanTask as any).categoryId;
      let finalSubcategoryId = (cleanTask as any).subcategoryId;
      
      // If IDs not set, find them from names
      if (!finalCategoryId && cleanTask.category) {
        const cat = uniqueCategories.find(c => c.name === cleanTask.category);
        if (cat) finalCategoryId = cat.id;
      }
      
      if (!finalSubcategoryId && cleanTask.subcategory) {
        const sub = subcategories.find(s => s.name === cleanTask.subcategory);
        if (sub) finalSubcategoryId = sub.id;
      }
      
      const taskData = { 
        ...cleanTask, 
        id,
        categoryId: finalCategoryId || '',
        subcategoryId: finalSubcategoryId || '',
        addedDate: cleanTask.addedDate || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        // Ensure validationRules is populated for workbench/review compatibility
        validationRules: Object.keys(validationRules).length > 0 ? validationRules : undefined,
      };
      
      // Final cleanup: remove any remaining undefined values
      Object.keys(taskData).forEach(key => {
        if (taskData[key as keyof typeof taskData] === undefined) {
          delete taskData[key as keyof typeof taskData];
        }
      });
      
      console.log("💾 [TaskModal] Saving task with validation data:", {
        expectedLabelsByRole: task.expectedLabelsByRole,
        validationByRole: task.validationByRole,
        validationRules: taskData.validationRules,
        submissionSpec: taskData.submissionSpec,
        taskKind: taskData.taskKind,
      });
      
      // Ensure submissionSpec.mediaSlots is properly structured
      if (taskData.taskKind === 'custom' && taskData.submissionSpec?.mediaSlots) {
        // Validate mediaSlots structure
        const validSlots = taskData.submissionSpec.mediaSlots.filter((slot: any) => 
          slot && typeof slot === 'object' && slot.role
        );
        if (validSlots.length === 0) {
          throw new Error("Custom task must have at least one step in mediaSlots");
        }
        taskData.submissionSpec.mediaSlots = validSlots;
      }
      
      await setDoc(doc(db, "tasks", id), taskData);
      alert(`Task ${mode === 'create' ? 'created' : 'updated'} successfully!`);
      onSave?.();
      onClose();
    } catch (error: any) {
      console.error("Error saving task:", error);
      const errorMessage = error?.message || error?.toString() || "Unknown error";
      console.error("Full error details:", {
        code: error?.code,
        message: errorMessage,
        stack: error?.stack
      });
      alert(`Error saving task: ${errorMessage}\n\nCheck the browser console for more details.`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!taskId) return;
    if (!window.confirm(`Are you sure you want to delete task "${task.title}"?`)) return;

    try {
      await deleteDoc(doc(db, "tasks", taskId));
      alert("Task deleted successfully!");
      onSave?.();
      onClose();
    } catch (error) {
      console.error("Error deleting task:", error);
      alert("Error deleting task. Please try again.");
    }
  };

  const handleOpenImageCrop = (target: 'icon' | StepKey, index: number = 0) => {
    setImageCropTarget(target);
    setImageCropIndex(index);
    setShowImageCropModal(true);
  };

  const handleImageCropSave = (imageUrl: string) => {
    if (!imageCropTarget) return;

    if (imageCropTarget === 'icon') {
      setTask(prev => ({ ...prev, icon: imageUrl }));
    } else if (imageCropTarget === 'primary' && task.taskKind === 'single') {
      // For single tasks, primary photo goes to sampleImageUrl
      setTask(prev => ({ ...prev, sampleImageUrl: imageUrl }));
    } else {
      // For primary (expiration/before_after), expiration_label, or after
      setSampleMediaAt(imageCropTarget, imageCropIndex, imageUrl);
    }
  };

  const handleVideoUploadClick = () => {
    videoInputRef.current?.click();
  };

  const handleVideoFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const inputEl = e.target;
    inputEl.value = '';

    setVideoUploading(true);
    const objectUrl = URL.createObjectURL(file);

    const checkDimensions = (): Promise<{ width: number; height: number }> => {
      return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.onloadedmetadata = () => {
          const width = video.videoWidth;
          const height = video.videoHeight;
          URL.revokeObjectURL(objectUrl);
          resolve({ width, height });
        };
        video.onerror = () => {
          URL.revokeObjectURL(objectUrl);
          reject(new Error('Failed to load video metadata'));
        };
        video.src = objectUrl;
      });
    };

    try {
      const { width, height } = await checkDimensions();
      if (width !== height) {
        alert(`Please upload a square (1:1) video. This video is ${width}x${height}.`);
        setVideoUploading(false);
        return;
      }

      const timestamp = Date.now();
      const fileName = `${timestamp}_${Math.random().toString(36).substring(7)}.mp4`;
      const storagePath = `task-images/${fileName}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      setTask(prev => ({ ...prev, sampleImageUrl: downloadURL }));
    } catch (err) {
      if (err instanceof Error && err.message !== 'Failed to load video metadata') {
        alert(err.message);
      } else {
        alert('Failed to process or upload video. Please try again.');
      }
    } finally {
      setVideoUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl my-8 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-bold">
            {mode === 'create' ? 'Create Task' : 'Edit Task'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'basic', label: 'Basic Settings' },
              { id: 'validation', label: 'Validation Rules' },
              { id: 'auto-approval', label: 'Auto-Approval' },
              { id: 'steps', label: 'Steps' },
              { id: 'translations', label: 'Translations' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : (
            <div className="space-y-6">
              {/* Basic Settings Tab */}
              {activeTab === 'basic' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Task Kind
                      </label>
                      <select
                        name="taskKind"
                        value={task.taskKind || 'single'}
                        onChange={handleTaskChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option key="taskkind-single" value="single">Single</option>
                        <option key="taskkind-expiration" value="expiration">Expiration (front + expiration label)</option>
                        <option key="taskkind-before_after" value="before_after">Before/After (before + after)</option>
                        <option key="taskkind-custom" value="custom">Custom (unlimited steps)</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Title <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={task.title}
                        onChange={handleTaskChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Category <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="category"
                        value={task.category}
                        onChange={handleTaskChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option key="category-empty" value="">Select Category</option>
                        {uniqueCategories.map((cat, index) => (
                          <option key={cat.id || `category-${index}`} value={cat.name}>{cat.name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Subcategory
                      </label>
                      <select
                        name="subcategory"
                        value={task.subcategory}
                        onChange={handleTaskChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={!task.category}
                      >
                        <option key="subcategory-empty" value="">Select Subcategory</option>
                        {filteredSubcategories.map((sub, index) => (
                          <option key={sub.id || `subcategory-${index}`} value={sub.name}>{sub.name || sub.id}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Item Type
                      </label>
                      <select
                        name="itemType"
                        value={task.itemType || ""}
                        onChange={handleTaskChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={!task.category || !task.subcategory}
                      >
                        <option key="itemtype-empty" value="">Select Item Type</option>
                        {(() => {
                          // Check if current task's itemType is in the filtered list (using normalized comparison)
                          const normalizedTaskItemType = task.itemType ? normalizeItemTypeName(task.itemType) : '';
                          const currentItemTypeInList = filteredItemTypes.some(it => normalizeItemTypeName(it.name) === normalizedTaskItemType);
                          const currentItemTypeExists = itemTypes.some(it => normalizeItemTypeName(it.name) === normalizedTaskItemType);
                          const needsWarning = task.itemType && currentItemTypeExists && !currentItemTypeInList;
                          
                          const options: React.ReactElement[] = [];
                          
                          // If task has an itemType that's not in the filtered list but exists in collection, add it with warning
                          if (needsWarning) {
                            options.push(
                              <option key={`current-${task.itemType}`} value={task.itemType}>
                                {task.itemType} (⚠️ Not linked to this category/subcategory - update on ItemTypes page)
                              </option>
                            );
                          }
                          
                          // Add all filtered item types
                          filteredItemTypes.forEach((itemType, index) => {
                            options.push(
                              <option key={itemType.id || `itemtype-${index}`} value={itemType.name}>
                                {itemType.name || itemType.id}
                              </option>
                            );
                          });
                          
                          return options;
                        })()}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Type
                      </label>
                      <select
                        name="type"
                        value={task.type}
                        onChange={handleTaskChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option key="type-photo" value="Photo">Photo</option>
                        <option key="type-video" value="Video">Video</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Visibility / Language
                      </label>
                      <select
                        name="visibilityLanguage"
                        value={task.visibility?.languages?.[0] ?? 'all'}
                        onChange={handleTaskChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {VISIBILITY_LANGUAGE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Condition
                      </label>
                      <input
                        type="text"
                        name="condition"
                        value={task.condition}
                        onChange={handleTaskChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        View
                      </label>
                      <input
                        type="text"
                        name="view"
                        value={task.view}
                        onChange={handleTaskChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Reward
                      </label>
                      <input
                        type="number"
                        name="reward"
                        value={task.reward}
                        onChange={handleTaskChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Cooldown (days)
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        value={cooldownDays}
                        onChange={(e) => {
                          const days = parseFloat(e.target.value) || 0;
                          setCooldownDays(days);
                          // Convert days to seconds
                          const seconds = Math.round(days * 24 * 60 * 60);
                          setTask((prev: any) => ({
                            ...prev,
                            cooldownSeconds: seconds
                          }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Max Submissions Per Cooldown
                      </label>
                      <input
                        type="number"
                        name="maxSubmissionsPerCooldown"
                        min="1"
                        value={task.maxSubmissionsPerCooldown ?? 1}
                        onChange={handleTaskChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Maximum number of submissions allowed within the cooldown period
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Icon Photo
                      </label>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleOpenImageCrop('icon')}
                          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                        >
                          {task.icon ? 'Change Icon' : 'Upload Icon'}
                        </button>
                        <div className="relative">
                          {task.icon ? (
                            <img 
                              src={task.icon} 
                              alt="Icon preview" 
                              className="h-16 w-16 object-contain border border-gray-300 rounded"
                            />
                          ) : (
                            <div className="h-16 w-16 border-2 border-dashed border-gray-300 rounded flex items-center justify-center bg-gray-50">
                              <span className="text-gray-400 text-xs text-center px-2">No Icon</span>
                            </div>
                          )}
                          {task.icon && (
                            <button
                              type="button"
                              onClick={() => setTask(prev => ({ ...prev, icon: '' }))}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      </div>
                      {task.icon && (
                        <input
                          type="text"
                          name="icon"
                          value={task.icon}
                          onChange={handleTaskChange}
                          placeholder="Or paste URL directly"
                          className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 mt-2"
                        />
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Mode
                      </label>
                      <select
                        name="mode"
                        value={task.mode}
                        onChange={handleTaskChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option key="mode-default" value="default">Default</option>
                        <option key="mode-test" value="test">Test Mode</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={task.description}
                      onChange={handleTaskChange}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {task.taskKind === 'single' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {task.type === 'Video' ? 'Primary Video (Step 1)' : 'Primary Photo (Step 1)'}
                      </label>
                      <div className="flex items-center gap-2">
                        {task.type === 'Video' ? (
                          <>
                            <input
                              ref={videoInputRef}
                              type="file"
                              accept="video/*"
                              onChange={handleVideoFileSelect}
                              className="hidden"
                            />
                            <button
                              type="button"
                              onClick={handleVideoUploadClick}
                              disabled={videoUploading}
                              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                            >
                              {videoUploading ? 'Uploading...' : (task.sampleImageUrl ? 'Change Video' : 'Upload Video')}
                            </button>
                            {task.sampleImageUrl && (
                              <div className="relative">
                                <video
                                  src={task.sampleImageUrl}
                                  className="h-24 w-24 object-contain border border-gray-300 rounded bg-black"
                                  muted
                                  loop
                                  playsInline
                                  preload="metadata"
                                />
                                <button
                                  type="button"
                                  onClick={() => setTask(prev => ({ ...prev, sampleImageUrl: '' }))}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                                >
                                  ×
                                </button>
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={() => handleOpenImageCrop('primary', 0)}
                              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                            >
                              {task.sampleImageUrl ? 'Change Photo' : 'Upload Photo'}
                            </button>
                            {task.sampleImageUrl && (
                              <div className="relative">
                                <img 
                                  src={task.sampleImageUrl} 
                                  alt="Primary photo preview" 
                                  className="h-24 w-24 object-contain border border-gray-300 rounded"
                                />
                                <button
                                  type="button"
                                  onClick={() => setTask(prev => ({ ...prev, sampleImageUrl: '' }))}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                                >
                                  ×
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                      {task.sampleImageUrl && (
                        <input
                          type="text"
                          name="sampleImageUrl"
                          value={task.sampleImageUrl}
                          onChange={handleTaskChange}
                          placeholder="Or paste URL directly"
                          className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 mt-2"
                        />
                      )}
                    </div>
                  )}

                  {task.taskKind === 'single' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Next Task
                      </label>
                      <div className="relative next-task-dropdown">
                        <div 
                          className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer bg-white"
                          onClick={() => setShowNextTaskDropdown(!showNextTaskDropdown)}
                        >
                          {task.nextTaskId ? (
                            <div className="flex items-center justify-between">
                              <span>
                                {allTasks.find(t => t.id === task.nextTaskId)?.title || task.nextTaskId}
                              </span>
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setTask(prev => ({ ...prev, nextTaskId: '' }));
                                  }}
                                  className="text-red-500 hover:text-red-700 text-sm"
                                >
                                  Clear
                                </button>
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between text-gray-500">
                              <span>Select Next Task (optional)</span>
                              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>
                          )}
                        </div>
                        
                        {showNextTaskDropdown && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
                            {/* Search Input */}
                            <div className="p-2 border-b border-gray-200">
                              <input
                                type="text"
                                placeholder="Search tasks..."
                                value={nextTaskSearchTerm}
                                onChange={(e) => setNextTaskSearchTerm(e.target.value)}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                            
                            {/* Task List */}
                            <div className="max-h-48 overflow-y-auto">
                              {filteredNextTasks.length > 0 ? (
                                filteredNextTasks.map(taskOption => (
                                  <div
                                    key={taskOption.id}
                                    className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                                    onClick={() => {
                                      setTask(prev => ({ ...prev, nextTaskId: taskOption.id }));
                                      setShowNextTaskDropdown(false);
                                      setNextTaskSearchTerm('');
                                    }}
                                  >
                                    <div className="font-medium">{taskOption.title}</div>
                                    <div className="text-xs text-gray-500">{taskOption.id}</div>
                                  </div>
                                ))
                              ) : (
                                <div className="px-3 py-2 text-gray-500 text-sm text-center">
                                  No tasks found
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center space-x-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="active"
                        checked={task.active}
                        onChange={handleTaskChange}
                        className="mr-2"
                      />
                      <span className="text-sm font-medium text-gray-700">Active</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="bounty"
                        checked={task.bounty}
                        onChange={handleTaskChange}
                        className="mr-2"
                      />
                      <span className="text-sm font-medium text-gray-700">Bounty</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="oneTime"
                        checked={task.oneTime || false}
                        onChange={handleTaskChange}
                        className="mr-2"
                      />
                      <span className="text-sm font-medium text-gray-700">One Time</span>
                    </label>
                  </div>

                  {/* Advanced Section */}
                  <details className="border rounded p-3 bg-gray-50">
                    <summary className="cursor-pointer font-semibold">Advanced</summary>
                    <details className="mt-2 border rounded p-3 bg-white">
                      <summary className="cursor-pointer font-semibold">Submission Spec Preview (read-only)</summary>
                      <pre className="mt-2 text-xs whitespace-pre-wrap">{JSON.stringify(task.submissionSpec || buildDefaultSubmissionSpec(task.taskKind || 'single'), null, 2)}</pre>
                    </details>
                  </details>

                  {/* Classification Configuration */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="text-lg font-semibold mb-4">Classification Configuration</h3>
                    <div className="space-y-4">
                      <label className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={task.classification?.enabled || false}
                          onChange={(e) => setTask(prev => ({
                            ...prev,
                            classification: {
                              ...prev.classification,
                              enabled: e.target.checked,
                              classId: e.target.checked ? prev.classification?.classId || '' : '',
                              includeInExports: e.target.checked ? (prev.classification?.includeInExports ?? true) : false
                            }
                          }))}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="text-sm font-medium text-gray-700">Enable Classification</span>
                      </label>
                      
                      {task.classification?.enabled && (
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Class ID <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={task.classification?.classId || ''}
                              onChange={(e) => setTask(prev => ({
                                ...prev,
                                classification: {
                                  ...prev.classification!,
                                  classId: e.target.value
                                }
                              }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="e.g., tomato_spoiled, banana_ripe, onion_fresh"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              This is the canonical class ID stored when submissions are approved. 
                              {task.category && task.itemType && task.condition && (
                                <span className="ml-2 text-blue-600">
                                  Suggested: {[task.category, task.itemType, task.condition]
                                    .filter(Boolean)
                                    .map(s => s.toLowerCase().replace(/\s+/g, '_'))
                                    .join('_')}
                                </span>
                              )}
                            </p>
                          </div>
                          
                          <div className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              checked={task.classification?.deriveFromFields || false}
                              onChange={(e) => setTask(prev => ({
                                ...prev,
                                classification: {
                                  ...prev.classification!,
                                  deriveFromFields: e.target.checked
                                }
                              }))}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <label className="text-sm font-medium text-gray-700">
                              Derive from Category/Item Type/Condition if empty
                            </label>
                          </div>
                          
                          <div className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              checked={task.classification?.includeInExports || false}
                              onChange={(e) => setTask(prev => ({
                                ...prev,
                                classification: {
                                  ...prev.classification!,
                                  includeInExports: e.target.checked
                                }
                              }))}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <label className="text-sm font-medium text-gray-700">
                              Include in exports (manifest.json, classification.csv)
                            </label>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Detection Configuration */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h3 className="text-lg font-semibold mb-4">Detection Configuration</h3>
                    <div className="space-y-4">
                      <label className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={task.detection?.enabled || false}
                          onChange={(e) => setTask(prev => ({
                            ...prev,
                            detection: {
                              ...prev.detection,
                              enabled: e.target.checked,
                              allowedCategories: e.target.checked ? (prev.detection?.allowedCategories || []) : [],
                              slaHours: e.target.checked ? (prev.detection?.slaHours || 24) : 24,
                              priority: e.target.checked ? (prev.detection?.priority || 3) : 3
                            }
                          }))}
                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                        />
                        <span className="text-sm font-medium text-gray-700">Enable Detection</span>
                      </label>
                      
                      {task.detection?.enabled && (
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Detection Schema <span className="text-red-500">*</span>
                            </label>
                            
                            {/* Searchable Schema Dropdown */}
                            <div className="relative schema-dropdown">
                              <div 
                                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500 cursor-pointer bg-white"
                                onClick={() => setShowSchemaDropdown(!showSchemaDropdown)}
                              >
                                {task.detection?.schemaRef?.id ? (
                                  <div className="flex items-center justify-between">
                                    <span>
                                      {schemas.find(s => s.id === task.detection?.schemaRef?.id)?.name || 'Unknown Schema'} 
                                      (v{schemas.find(s => s.id === task.detection?.schemaRef?.id)?.version || 1})
                                    </span>
                                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                  </div>
                                ) : (
                                  <div className="flex items-center justify-between text-gray-500">
                                    <span>Select Detection Schema</span>
                                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                  </div>
                                )}
                              </div>
                              
                              {showSchemaDropdown && (
                                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
                                  {/* Search Input */}
                                  <div className="p-2 border-b border-gray-200">
                                    <input
                                      type="text"
                                      placeholder="Search schemas..."
                                      value={schemaSearchTerm}
                                      onChange={(e) => setSchemaSearchTerm(e.target.value)}
                                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  </div>
                                  
                                  {/* Schema List */}
                                  <div className="max-h-48 overflow-y-auto">
                                    {filteredSchemas.length > 0 ? (
                                      filteredSchemas.map(schema => (
                                        <div
                                          key={schema.id}
                                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                                          onClick={() => {
                                            setTask(prev => ({
                                              ...prev,
                                              detection: {
                                                ...prev.detection!,
                                                schemaRef: {
                                                  id: schema.id,
                                                  version: schema.version?.toString() || '1'
                                                }
                                              }
                                            }));
                                            setShowSchemaDropdown(false);
                                            setSchemaSearchTerm('');
                                          }}
                                        >
                                          <div className="font-medium text-sm text-gray-900">{schema.name}</div>
                                          <div className="text-xs text-gray-500">
                                            v{schema.version || 1} • {schema.classes?.length || 0} classes
                                            {schema.includeGlobalCategories && ' • Includes global objects'}
                                          </div>
                                          {schema.guidelines && (
                                            <div className="text-xs text-gray-400 mt-1 truncate">
                                              {schema.guidelines.substring(0, 100)}...
                                            </div>
                                          )}
                                        </div>
                                      ))
                                    ) : (
                                      <div className="px-3 py-2 text-sm text-gray-500 text-center">
                                        {schemaSearchTerm ? 'No schemas found matching your search' : 'No detection schemas available'}
                                      </div>
                                    )}
                                  </div>
                                  
                                  {schemas.length === 0 && (
                                    <div className="px-3 py-2 text-sm text-blue-600 border-t border-gray-200">
                                      <div className="text-center">
                                        <p className="mb-2">No detection schemas found</p>
                                        <a href="/labeling/schemas" className="underline hover:text-blue-800">
                                          Go to Detection Setup to create schemas
                                        </a>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            
                            <p className="text-xs text-gray-500 mt-1">
                              Select a schema that includes shapes (bounding boxes, polygons) for object detection.
                            </p>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                SLA Hours
                              </label>
                              <input
                                type="number"
                                value={task.detection?.slaHours || 24}
                                onChange={(e) => setTask(prev => ({
                                  ...prev,
                                  detection: {
                                    ...prev.detection!,
                                    slaHours: parseInt(e.target.value) || 24
                                  }
                                }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                                min="1"
                                max="168"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Priority (1-5)
                              </label>
                              <input
                                type="number"
                                value={task.detection?.priority || 3}
                                onChange={(e) => setTask(prev => ({
                                  ...prev,
                                  detection: {
                                    ...prev.detection!,
                                    priority: Math.max(1, Math.min(5, parseInt(e.target.value) || 3))
                                  }
                                }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                                min="1"
                                max="5"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Validation Rules Tab */}
              {activeTab === 'validation' && (
                <div className="space-y-4">
                  {/* Validation Prompt - Always shown for all tasks */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Validation Prompt
                    </label>
                    <input 
                      name="validationPrompt" 
                      value={task.validationPrompt || ''} 
                      onChange={handleTaskChange} 
                      placeholder="Validation Prompt (e.g., A clear photo showing Fresh Apples with vibrant red color and firm texture)" 
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Role-specific validation - Always shown for all tasks */}
                  <div className="space-y-4">
                    <div className="text-sm font-medium text-gray-700">Role-specific validation</div>
                    {/* Dev-only debug line */}
                    {process.env.NODE_ENV !== 'production' && (
                      <div className="text-xs text-gray-500 bg-gray-100 p-2 rounded">
                        Step keys: {getTaskStepKeys(task).join(', ')}
                      </div>
                    )}
                    {(() => {
                      // Use step slots from helper (single source of truth)
                      const stepSlots = getTaskStepSlots(task);
                      
                      return stepSlots.map((slot, index) => {
                        const stepKey = slot.role;
                        // Handle customValidation - it might be an object or a string
                        const customValidationRaw = (((task.validationByRole || {})[stepKey] || {}).customValidation);
                        let customValidationValue = '';
                        if (customValidationRaw) {
                          if (typeof customValidationRaw === 'object') {
                            // If it's an object, stringify it
                            customValidationValue = JSON.stringify(customValidationRaw, null, 2);
                          } else if (typeof customValidationRaw === 'string') {
                            // If it's already a string, use it as-is
                            customValidationValue = customValidationRaw;
                          } else {
                            customValidationValue = String(customValidationRaw);
                          }
                        }
                        const isJsonValid = validateJSON(customValidationValue);
                        const isAdvancedOpen = expandedAdvancedRoles.has(stepKey);
                        const stepDisplayLabel = getStepDisplayLabel(task, stepKey, index);
                        // Canonical role keys: primary, expiration_label, after (Step 1 = primary, Step 2 = expiration_label or after)
                        return (
                          <div key={stepKey} className="border rounded p-4 bg-white">
                            <div className="font-semibold mb-3 text-gray-800">
                              {stepDisplayLabel} <span className="text-gray-500 font-normal text-sm">({stepKey})</span>
                            </div>

                            {/* Validation Rules Section (Always Open) */}
                            <div className="mb-4">
                              <div className="text-sm font-medium text-gray-700 mb-2">Validation Rules</div>
                              
                              <div className="space-y-3">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Expected Labels
                                  </label>
                                  
                                  {/* Selected Labels as Chips */}
                                  <div className="flex flex-wrap gap-2 mb-2 min-h-[2.5rem] p-2 border border-gray-200 rounded bg-gray-50">
                                    {((task.expectedLabelsByRole || {})[stepKey] || []).length > 0 ? (
                                      ((task.expectedLabelsByRole || {})[stepKey] || []).map((labelId: string) => (
                                        <span
                                          key={labelId}
                                          className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                                        >
                                          <span>{getLabelDisplay(labelId)}</span>
                                          <button
                                            type="button"
                                            onClick={() => removeExpectedLabel(stepKey, labelId)}
                                            className="ml-1 text-blue-600 hover:text-blue-800 focus:outline-none"
                                            aria-label={`Remove ${getLabelDisplay(labelId)}`}
                                          >
                                            ×
                                          </button>
                                        </span>
                                      ))
                                    ) : (
                                      <span className="text-gray-400 text-sm">No labels selected</span>
                                    )}
                                  </div>
                                  
                                  {/* Search Input */}
                                  <div className="relative" ref={(el) => {
                                    expectedLabelDropdownRefs.current[stepKey] = el;
                                  }}>
                                    <input
                                      type="text"
                                      value={expectedLabelSearchTerms[stepKey] || ''}
                                      onChange={(e) => {
                                        setExpectedLabelSearchTerms(prev => ({ ...prev, [stepKey]: e.target.value }));
                                        setShowExpectedLabelDropdowns(prev => ({ ...prev, [stepKey]: true }));
                                      }}
                                      onFocus={() => setShowExpectedLabelDropdowns(prev => ({ ...prev, [stepKey]: true }))}
                                      placeholder="Search and select labels..."
                                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                    
                                    {/* Dropdown */}
                                    {showExpectedLabelDropdowns[stepKey] && (
                                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-y-auto">
                                        {getFilteredLabels(stepKey).length > 0 ? (
                                          getFilteredLabels(stepKey)
                                            .filter(label => !((task.expectedLabelsByRole || {})[stepKey] || []).includes(label.id))
                                            .map(label => (
                                              <button
                                                key={label.id}
                                                type="button"
                                                onClick={() => addExpectedLabel(stepKey, label.id)}
                                                className="w-full text-left px-4 py-2 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none"
                                              >
                                                <div className="flex items-center justify-between">
                                                  <span className="text-sm">{label.display}</span>
                                                  <span className="text-xs text-gray-500">{label.source}</span>
                                                </div>
                                              </button>
                                            ))
                                        ) : (
                                          <div className="px-4 py-2 text-sm text-gray-500">
                                            {expectedLabelSearchTerms[stepKey] ? 'No labels found' : 'Start typing to search...'}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  
                                  {getAvailableLabels.length === 0 && (
                                    <p className="text-xs text-gray-500 mt-1">
                                      No labels available. Select a detection schema to see available labels.
                                    </p>
                                  )}
                                </div>

                              </div>
                            </div>

                            {/* Advanced Section (Collapsible) */}
                            <div className="border-t border-gray-200 pt-3">
                              <button
                                type="button"
                                onClick={() => toggleAdvancedRole(stepKey)}
                                className="w-full flex items-center justify-between font-medium text-gray-700 mb-3 hover:text-gray-900 focus:outline-none"
                              >
                                <span>Advanced</span>
                                <span className="text-gray-400">{isAdvancedOpen ? '▼' : '▶'}</span>
                              </button>
                              
                              {isAdvancedOpen && (
                                <div className="space-y-3">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Custom Validation (JSON)
                                      {customValidationValue && !isJsonValid && (
                                        <span className="ml-2 text-xs text-red-600">⚠ Invalid JSON</span>
                                      )}
                                      {customValidationValue && isJsonValid && (
                                        <span className="ml-2 text-xs text-green-600">✓ Valid JSON</span>
                                      )}
                                    </label>
                                    <textarea
                                      value={customValidationValue}
                                      onChange={(e) => setValidationByRoleField(stepKey, 'customValidation', e.target.value)}
                                      className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                        customValidationValue && !isJsonValid ? 'border-red-300' : 'border-gray-300'
                                      }`}
                                      rows={3}
                                      placeholder='{"rule":"..."}'
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      });
                    })()}
                    <div className="text-xs text-gray-500 mt-2">
                      Note: task-level validation fields remain unchanged; these are additive `validationByRole` / `expectedLabelsByRole`.
                    </div>
                  </div>
                </div>
              )}

              {/* Auto-Approval Tab */}
              {activeTab === 'auto-approval' && (
                <div className="space-y-6">
                  {/* Enable Auto-Approval */}
                  <div className="flex items-center space-x-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <input
                      type="checkbox"
                      checked={task.autoRules?.enabled || false}
                      onChange={(e) => setTask(prev => ({
                        ...prev,
                        autoRules: {
                          ...prev.autoRules,
                          enabled: e.target.checked,
                          modelId: e.target.checked ? prev.autoRules?.modelId || '' : '',
                          approveAt: e.target.checked ? prev.autoRules?.approveAt || 0.92 : 0.92,
                          rejectAt: e.target.checked ? prev.autoRules?.rejectAt || 0.10 : 0.10,
                          auditRate: e.target.checked ? prev.autoRules?.auditRate || 20 : 20,
                          dailyCap: e.target.checked ? prev.autoRules?.dailyCap || 200 : 200,
                          canaryPct: e.target.checked ? prev.autoRules?.canaryPct || 10 : 10
                        }
                      }))}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-700">Enable Auto-Approval</span>
                      <p className="text-xs text-gray-500 mt-1">
                        Automatically approve or reject submissions based on AI model confidence scores
                      </p>
                    </div>
                  </div>

                  {task.autoRules?.enabled && (
                    <div className="space-y-6">
                      {/* Model ID */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Model ID
                        </label>
                        <input
                          type="text"
                          value={task.autoRules?.modelId || ''}
                          onChange={(e) => setTask(prev => ({
                            ...prev,
                            autoRules: {
                              ...prev.autoRules!,
                              modelId: e.target.value
                            }
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., model_v1_20240101"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          The AI model ID to use for auto-approval decisions
                        </p>
                      </div>

                      {/* Thresholds */}
                      <div>
                        <h3 className="text-sm font-semibold text-gray-700 mb-3">Confidence Thresholds</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Approve When Score ≥
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              max="1"
                              value={task.autoRules?.approveAt || 0.92}
                              onChange={(e) => setTask(prev => ({
                                ...prev,
                                autoRules: {
                                  ...prev.autoRules!,
                                  approveAt: parseFloat(e.target.value) || 0.92
                                }
                              }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Submissions with AI score above this threshold will be auto-approved
                            </p>
                          </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Min Confidence (0-1)
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            max="1"
                            value={String(task.autoRules?.minConfidence ?? '')}
                            onChange={(e) => setTask((prev: any) => ({
                              ...prev,
                              autoRules: {
                                ...(prev.autoRules || {}),
                                minConfidence: e.target.value === '' ? undefined : Number(e.target.value)
                              }
                            }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="0.7"
                          />
                        </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Reject When Score ≤
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              max="1"
                              value={task.autoRules?.rejectAt || 0.10}
                              onChange={(e) => setTask(prev => ({
                                ...prev,
                                autoRules: {
                                  ...prev.autoRules!,
                                  rejectAt: parseFloat(e.target.value) || 0.10
                                }
                              }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Submissions with AI score below this threshold will be auto-rejected
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Audit and Limits */}
                      <div>
                        <h3 className="text-sm font-semibold text-gray-700 mb-3">Audit and Limits</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Audit Rate (1 in N)
                            </label>
                            <input
                              type="number"
                              min="1"
                              max="100"
                              value={task.autoRules?.auditRate || 20}
                              onChange={(e) => setTask(prev => ({
                                ...prev,
                                autoRules: {
                                  ...prev.autoRules!,
                                  auditRate: parseInt(e.target.value) || 20
                                }
                              }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Randomly route 1 in N AI decisions to human review for quality assurance
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Daily Cap
                            </label>
                            <input
                              type="number"
                              min="1"
                              value={task.autoRules?.dailyCap || 200}
                              onChange={(e) => setTask(prev => ({
                                ...prev,
                                autoRules: {
                                  ...prev.autoRules!,
                                  dailyCap: parseInt(e.target.value) || 200
                                }
                              }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Maximum number of AI approvals allowed per day
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Canary Percentage (%)
                            </label>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              value={task.autoRules?.canaryPct || 10}
                              onChange={(e) => setTask(prev => ({
                                ...prev,
                                autoRules: {
                                  ...prev.autoRules!,
                                  canaryPct: parseInt(e.target.value) || 10
                                }
                              }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Percentage of submissions to route to canary model for testing
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Metadata (read-only if exists) */}
                      {(task.autoRules?.updatedAt || task.autoRules?.updatedBy) && (
                        <div className="bg-gray-50 p-3 rounded border border-gray-200">
                          <p className="text-xs text-gray-600">
                            {task.autoRules?.updatedBy && (
                              <span>Last updated by: <strong>{task.autoRules.updatedBy}</strong></span>
                            )}
                            {task.autoRules?.updatedAt && task.autoRules?.updatedBy && ' • '}
                            {task.autoRules?.updatedAt && (
                              <span>Updated: {task.autoRules.updatedAt instanceof Date 
                                ? task.autoRules.updatedAt.toLocaleString() 
                                : typeof task.autoRules.updatedAt === 'object' && task.autoRules.updatedAt !== null
                                ? 'Recently'
                                : new Date(task.autoRules.updatedAt).toLocaleString()}
                              </span>
                            )}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Steps Tab */}
              {activeTab === 'steps' && (
                <div className="space-y-4">
                  {task.taskKind === 'single' ? (
                    <div className="text-sm text-gray-600">
                      Steps configuration is only available for multi-photo tasks (expiration, before_after, or custom).
                    </div>
                  ) : task.taskKind === 'custom' ? (
                    <div className="space-y-4">
                      {/* Dev-only debug line */}
                      {process.env.NODE_ENV !== 'production' && (
                        <div className="text-xs text-gray-500 bg-gray-100 p-2 rounded">
                          Step keys: {getTaskStepKeys(task).join(', ')}
                        </div>
                      )}
                      
                      {/* Custom Step Editor */}
                      <div className="space-y-6">
                        <div className="flex items-center justify-between">
                          <h3 className="text-lg font-semibold">Custom Steps</h3>
                          <button
                            type="button"
                            onClick={addCustomStep}
                            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                          >
                            Add Step
                          </button>
                        </div>
                        
                        {getTaskStepSlots(task).map((slot, index) => (
                          <div key={slot.role}>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Step {index + 1}: {slot.prompt || `Step ${index + 1}`}
                            </label>
                            <div className="flex gap-4">
                              {/* Image Preview - Left Side */}
                              <div className="w-1/2">
                                <div className="relative">
                                  {task.sampleMedia?.[slot.role]?.[0] ? (
                                    <img 
                                      src={task.sampleMedia[slot.role]?.[0] || ''} 
                                      alt={`Step ${index + 1} preview`} 
                                      className="w-full h-48 object-contain border border-gray-300 rounded bg-gray-50"
                                    />
                                  ) : (
                                    <div className="w-full h-48 border-2 border-dashed border-gray-300 rounded flex items-center justify-center bg-gray-50">
                                      <span className="text-gray-400 text-sm">No Image</span>
                                    </div>
                                  )}
                                  {task.sampleMedia?.[slot.role]?.[0] && (
                                    <button
                                      type="button"
                                      onClick={() => setSampleMediaAt(slot.role, 0, '')}
                                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                                    >
                                      ×
                                    </button>
                                  )}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleOpenImageCrop(slot.role, 0)}
                                  className="w-full mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                                >
                                  {(task.sampleMedia?.[slot.role]?.[0]) ? 'Change Photo' : 'Upload Photo'}
                                </button>
                                {task.sampleMedia?.[slot.role]?.[0] && (
                                  <input
                                    type="text"
                                    value={task.sampleMedia[slot.role]?.[0] || ''}
                                    onChange={(e) => setSampleMediaAt(slot.role, 0, e.target.value)}
                                    placeholder="Or paste URL directly"
                                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 mt-2 text-sm"
                                  />
                                )}
                              </div>
                              {/* Title and Description - Right Side */}
                              <div className="w-1/2 space-y-2">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Prompt/Label
                                  </label>
                                  <input
                                    type="text"
                                    value={slot.prompt || ''}
                                    onChange={(e) => updateCustomStepSlot(slot.role, 'prompt', e.target.value)}
                                    placeholder={`Step ${index + 1}`}
                                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  />
                                </div>
                                <div>
                                  <label className="flex items-center space-x-2">
                                    <input
                                      type="checkbox"
                                      checked={slot.required !== false}
                                      onChange={(e) => updateCustomStepSlot(slot.role, 'required', e.target.checked)}
                                      className="rounded"
                                    />
                                    <span className="text-sm font-medium text-gray-700">Required</span>
                                  </label>
                                </div>
                                <input
                                  value={task.submissionSteps?.[slot.role]?.title || ''}
                                  onChange={(e) => setSubmissionStepField(slot.role, 'title', e.target.value)}
                                  placeholder="Step Title"
                                  className="p-2 border w-full text-sm"
                                />
                                <textarea
                                  value={task.submissionSteps?.[slot.role]?.description || ''}
                                  onChange={(e) => setSubmissionStepField(slot.role, 'description', e.target.value)}
                                  placeholder="Step Description"
                                  rows={6}
                                  className="p-2 border w-full text-sm resize-none"
                                />
                              </div>
                            </div>
                            <div className="flex justify-end mt-2">
                              <button
                                type="button"
                                onClick={() => removeCustomStep(slot.role)}
                                disabled={getTaskStepSlots(task).length === 1}
                                className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                              >
                                Remove Step
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Primary Photo Section */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {task.taskKind === 'before_after' ? 'Step 1: Before Photo' : 'Step 1: Product Photo'}
                        </label>
                        <div className="flex gap-4">
                          {/* Image Preview - Left Side */}
                          <div className="w-1/2">
                            <div className="relative">
                              {task.sampleMedia?.primary?.[0] ? (
                                <img 
                                  src={task.sampleMedia.primary[0]} 
                                  alt="Primary photo preview" 
                                  className="w-full h-48 object-contain border border-gray-300 rounded bg-gray-50"
                                />
                              ) : (
                                <div className="w-full h-48 border-2 border-dashed border-gray-300 rounded flex items-center justify-center bg-gray-50">
                                  <span className="text-gray-400 text-sm">No Image</span>
                                </div>
                              )}
                              {task.sampleMedia?.primary?.[0] && (
                                <button
                                  type="button"
                                  onClick={() => setSampleMediaAt('primary', 0, '')}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                                >
                                  ×
                                </button>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => handleOpenImageCrop('primary', 0)}
                              className="w-full mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                            >
                              {(task.sampleMedia?.primary?.[0]) ? 'Change Photo' : 'Upload Photo'}
                            </button>
                            {task.sampleMedia?.primary?.[0] && (
                              <input
                                type="text"
                                value={task.sampleMedia.primary[0]}
                                onChange={(e) => setSampleMediaAt('primary', 0, e.target.value)}
                                placeholder="Or paste URL directly"
                                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 mt-2 text-sm"
                              />
                            )}
                          </div>
                          {/* Title and Description - Right Side */}
                          <div className="w-1/2 space-y-2">
                            <input
                              value={task.submissionSteps?.primary?.title || ''}
                              onChange={(e) => setSubmissionStepField('primary', 'title', e.target.value)}
                              placeholder="Step Title"
                              className="p-2 border w-full text-sm"
                            />
                            <textarea
                              value={task.submissionSteps?.primary?.description || ''}
                              onChange={(e) => setSubmissionStepField('primary', 'description', e.target.value)}
                              placeholder="Step Description"
                              rows={6}
                              className="p-2 border w-full text-sm resize-none"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Step 2 Photo Section (Expiration Label or After) */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {task.taskKind === 'expiration' ? 'Step 2: Expiration Label Photo' : 'Step 2: After Photo'}
                        </label>
                        <div className="flex gap-4">
                          {/* Image Preview - Left Side */}
                          <div className="w-1/2">
                            <div className="relative">
                              {((task.taskKind === 'expiration' ? task.sampleMedia?.expiration_label?.[0] : task.sampleMedia?.after?.[0])) ? (
                                <img 
                                  src={(task.taskKind === 'expiration' ? task.sampleMedia?.expiration_label?.[0] : task.sampleMedia?.after?.[0]) || ''} 
                                  alt={task.taskKind === 'expiration' ? 'Expiration label preview' : 'After photo preview'} 
                                  className="w-full h-48 object-contain border border-gray-300 rounded bg-gray-50"
                                />
                              ) : (
                                <div className="w-full h-48 border-2 border-dashed border-gray-300 rounded flex items-center justify-center bg-gray-50">
                                  <span className="text-gray-400 text-sm">No Image</span>
                                </div>
                              )}
                              {((task.taskKind === 'expiration' ? task.sampleMedia?.expiration_label?.[0] : task.sampleMedia?.after?.[0])) && (
                                <button
                                  type="button"
                                  onClick={() => setSampleMediaAt(task.taskKind === 'expiration' ? 'expiration_label' : 'after', 0, '')}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                                >
                                  ×
                                </button>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => handleOpenImageCrop(task.taskKind === 'expiration' ? 'expiration_label' : 'after', 0)}
                              className="w-full mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                            >
                              {((task.taskKind === 'expiration' ? task.sampleMedia?.expiration_label?.[0] : task.sampleMedia?.after?.[0])) ? 'Change Photo' : 'Upload Photo'}
                            </button>
                            {((task.taskKind === 'expiration' ? task.sampleMedia?.expiration_label?.[0] : task.sampleMedia?.after?.[0])) && (
                              <input
                                type="text"
                                value={(task.taskKind === 'expiration' ? task.sampleMedia?.expiration_label?.[0] : task.sampleMedia?.after?.[0]) || ''}
                                onChange={(e) => setSampleMediaAt(task.taskKind === 'expiration' ? 'expiration_label' : 'after', 0, e.target.value)}
                                placeholder="Or paste URL directly"
                                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 mt-2 text-sm"
                              />
                            )}
                          </div>
                          {/* Title and Description - Right Side */}
                          <div className="w-1/2 space-y-2">
                            <input
                              value={
                                task.taskKind === 'expiration'
                                  ? (task.submissionSteps?.expiration_label?.title || '')
                                  : (task.submissionSteps?.after?.title || '')
                              }
                              onChange={(e) => setSubmissionStepField(task.taskKind === 'expiration' ? 'expiration_label' : 'after', 'title', e.target.value)}
                              placeholder="Step Title"
                              className="p-2 border w-full text-sm"
                            />
                            <textarea
                              value={
                                task.taskKind === 'expiration'
                                  ? (task.submissionSteps?.expiration_label?.description || '')
                                  : (task.submissionSteps?.after?.description || '')
                              }
                              onChange={(e) => setSubmissionStepField(task.taskKind === 'expiration' ? 'expiration_label' : 'after', 'description', e.target.value)}
                              placeholder="Step Description"
                              rows={6}
                              className="p-2 border w-full text-sm resize-none"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

              {/* Translations Tab */}
              {activeTab === 'translations' && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">Add or edit translated titles and descriptions for this task. English is the default (main title/description).</p>
                  {/* Existing translations list */}
                  {task.i18n && (Object.keys(task.i18n.title || {}).length > 0 || Object.keys(task.i18n.description || {}).length > 0) && (
                    <div className="space-y-2">
                      <h4 className="font-medium text-gray-700">Existing translations</h4>
                      {Array.from(new Set([
                        ...Object.keys(task.i18n.title || {}),
                        ...Object.keys(task.i18n.description || {}),
                      ])).map((lang) => {
                        const option = TRANSLATION_LANGUAGE_OPTIONS.find((o) => o.value === lang) || { value: lang, label: lang };
                        const isEditing = editingTranslationLang === lang;
                        return (
                          <div key={lang} className="border rounded p-3 bg-gray-50">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium">{option.label} ({lang})</span>
                              <div className="flex gap-2">
                                {!isEditing ? (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setEditingTranslationLang(lang);
                                        setEditTranslationTitle((task.i18n?.title || {})[lang] || '');
                                        setEditTranslationDescription((task.i18n?.description || {})[lang] || '');
                                      }}
                                      className="px-2 py-1 text-sm bg-blue-500 text-white rounded"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => window.confirm('Delete this translation?') && deleteTranslation(lang)}
                                      className="px-2 py-1 text-sm bg-red-500 text-white rounded"
                                    >
                                      Delete
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => updateTranslation(lang)}
                                      className="px-2 py-1 text-sm bg-green-500 text-white rounded"
                                    >
                                      Save
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setEditingTranslationLang(null);
                                        setEditTranslationTitle('');
                                        setEditTranslationDescription('');
                                      }}
                                      className="px-2 py-1 text-sm bg-gray-500 text-white rounded"
                                    >
                                      Cancel
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                            {isEditing ? (
                              <div className="space-y-2 mt-2">
                                <input
                                  type="text"
                                  value={editTranslationTitle}
                                  onChange={(e) => setEditTranslationTitle(e.target.value)}
                                  placeholder="Translated title"
                                  className="w-full px-3 py-2 border rounded"
                                />
                                <textarea
                                  value={editTranslationDescription}
                                  onChange={(e) => setEditTranslationDescription(e.target.value)}
                                  placeholder="Translated description"
                                  rows={3}
                                  className="w-full px-3 py-2 border rounded"
                                />
                              </div>
                            ) : (
                              <>
                                <p className="text-sm text-gray-700"><strong>Title:</strong> {(task.i18n?.title || {})[lang] || '—'}</p>
                                <p className="text-sm text-gray-700 mt-1"><strong>Description:</strong> {(task.i18n?.description || {})[lang] || '—'}</p>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {/* Add new translation */}
                  <div className="border rounded p-4 bg-white">
                    <h4 className="font-medium text-gray-700 mb-2">Add translation</h4>
                    <div className="grid grid-cols-1 gap-2">
                      <select
                        value={newTranslationLang}
                        onChange={(e) => setNewTranslationLang(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded"
                      >
                        <option value="">Select language</option>
                        {TRANSLATION_LANGUAGE_OPTIONS.filter((o) => !(task.i18n?.title?.[o.value] || task.i18n?.description?.[o.value])).map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                        {TRANSLATION_LANGUAGE_OPTIONS.every((o) => task.i18n?.title?.[o.value] || task.i18n?.description?.[o.value]) && (
                          <option value="">All languages added</option>
                        )}
                      </select>
                      <input
                        type="text"
                        value={newTranslationTitle}
                        onChange={(e) => setNewTranslationTitle(e.target.value)}
                        placeholder="Translated title"
                        className="w-full px-3 py-2 border border-gray-300 rounded"
                      />
                      <textarea
                        value={newTranslationDescription}
                        onChange={(e) => setNewTranslationDescription(e.target.value)}
                        placeholder="Translated description"
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded"
                      />
                      <button
                        type="button"
                        onClick={addTranslation}
                        disabled={!newTranslationLang.trim()}
                        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                      >
                        Add translation
                      </button>
                    </div>
                  </div>
                </div>
              )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50">
          <div>
            {mode === 'edit' && (
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Delete
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving || loading}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
            >
              {saving ? 'Saving...' : mode === 'create' ? 'Create' : 'Update'}
            </button>
          </div>
        </div>
      </div>

      {/* Image Crop Modal */}
      <ImageCropModal
        isOpen={showImageCropModal}
        onClose={() => {
          setShowImageCropModal(false);
          setImageCropTarget(null);
          setImageCropIndex(0);
        }}
        onSave={(imageUrl) => {
          handleImageCropSave(imageUrl);
          setShowImageCropModal(false);
          setImageCropTarget(null);
          setImageCropIndex(0);
        }}
        aspectRatio={1}
        title={
          imageCropTarget === 'icon' 
            ? 'Crop Icon Photo' 
            : imageCropTarget === 'primary'
            ? task.taskKind === 'before_after' 
              ? 'Crop Step 1: Before Photo'
              : 'Crop Step 1: Product Photo'
            : imageCropTarget === 'expiration_label'
            ? 'Crop Step 2: Expiration Label Photo'
            : imageCropTarget === 'after'
            ? 'Crop Step 2: After Photo'
            : imageCropTarget && task.taskKind === 'custom'
            ? (() => {
                const stepSlots = getTaskStepSlots(task);
                const slot = stepSlots.find(s => s.role === imageCropTarget);
                const stepIndex = stepSlots.findIndex(s => s.role === imageCropTarget);
                const stepTitle = task.submissionSteps?.[imageCropTarget]?.title;
                const prompt = slot?.prompt || imageCropTarget;
                return stepTitle 
                  ? `Crop Step ${stepIndex + 1}: ${stepTitle}`
                  : `Crop Step ${stepIndex + 1}: ${prompt}`;
              })()
            : 'Crop Image'
        }
      />
    </div>
  );
}

